import React, { useState, useEffect } from "react";
import DailyReportService from "../../services/DailyReportService";
import XLSX from "xlsx-js-style";
import Breadcrumb from "../../components/Breadcrumb";
import ProjectService from "../../services/ProjectService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ProjectLocationService from "../../services/ProjectLocationService";
import StorageService from "../../services/StorageService";
import utility from "../../utils/utility";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo } from "../../utils/pdfLogo";
import MessageDialog from "../../components/MessageDialog";


export default function DailyProgressReport() {

    const [date, setDate] = useState("");
    const [reports, setReports] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [projects, setProjects] = useState([]);
    const [projectCategories, setProjectCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [msgType, setMsgType] = useState("");

    const showMessage = (text, type = "error") => {
        setMessage(text);
        setMsgType(type);
    };

    const handleClearFilters = () => {
        setDate("");
        setSelectedProject("");
        setSelectedLocation("");
        setLocations([]);
        setReports([]);
        setSearched(false);
    };
    const formatDate = (d) => {
        return new Date(d).toLocaleDateString("en-GB");
    };

    const formatProjectDisplay = (project) => {
        const safeProject = typeof project === "string"
            ? { projectName: project }
            : (project || {});

        if (!safeProject) return "-";

        const projectName = safeProject.projectName || safeProject.name || "";
        const categoryName = safeProject.projectCategoryName || safeProject.categoryName || safeProject.category || "";
        const projectCode = safeProject.projectCode || "";
        const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

        if (projectName) {
            if (categoryInitial) {
                return `${categoryInitial}*${projectName}`;
            }

            if (projectCode) {
                const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
                return `${codeInitial}*${projectName}`;
            }

            return projectName;
        }

        if (projectCode) {
            const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
            return `${codeInitial}*`;
        }

        return categoryInitial ? `${categoryInitial}*` : "-";
    };


    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            const comp = StorageService.activeCompany();
            const emp = StorageService.loginData();

            let projectList = [];
            let catList = [];

            try {
                const catRes = await ProjectCategoryService.getAll();
                catList = catRes?.data || catRes || [];
                setProjectCategories(catList);
            } catch (catErr) {
                console.error("ProjectCategory load error:", catErr);
            }

            const isSuperAdmin =
                emp.roleName === "Super Admin" ||
                emp.roleName === "SuperAdmin" ||
                Number(emp.roleId) === 1 ||
                Number(emp.roleId) === 2;

            if (isSuperAdmin) {
                const res = await ProjectService.getActive(comp.companyId);
                projectList = Array.isArray(res) ? res : (res?.data || []);
            } else {
                const res = await ProjectService.getAssignedProjects(
                    comp.companyId,
                    emp.employeeId
                );
                projectList = Array.isArray(res?.data?.data)
                    ? res.data.data
                    : Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res)
                    ? res
                    : [];
            }

            const projectsData = (projectList || []).map(p => {
                const categoryId = p.projectCategoryId || p.categoryId || p.projectCategoryID;
                const foundCat = catList.find(c => String(c.id || c.projectCategoryId) === String(categoryId));
                const categoryName = p.projectCategoryName || p.categoryName || p.category || foundCat?.categoryName || foundCat?.name || "";
                return {
                    ...p,
                    projectId: p.id || p.projectId,
                    projectName: p.projectName || p.name || "",
                    projectCategoryName: categoryName,
                    projectCode: p.projectCode || ""
                };
            });

            console.log("PROJECT LIST:", projectsData);

            setProjects(projectsData);

        } catch (err) {
            console.error("Project load error:", err);
            setProjects([]);
        }
    };

    const handleProjectChange = (e) => {
        const projId = e.target.value;
        setSelectedProject(projId);
        setSelectedLocation("");
        if (projId) {
            loadLocationsByProject(projId);
        } else {
            setLocations([]);
        }
    };

    const loadLocationsByProject = async (projId) => {
        if (!projId) {
            setLocations([]);
            return;
        }
        try {
            const res = await ProjectLocationService.getByProjectId(projId);
            const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            
            // Deduplicate all locations assigned to this project
            const uniqueLocations = [];
            const seen = new Set();
            list.forEach(loc => {
                const locId = loc.locationId || loc.id;
                const locName = loc.locationName || loc.name;
                if (locId && !seen.has(String(locId))) {
                    seen.add(String(locId));
                    uniqueLocations.push({
                        locationId: locId,
                        locationName: locName
                    });
                }
            });

            setLocations(uniqueLocations);
        } catch (err) {
            console.error("Location load error:", err);
            setLocations([]);
        }
    };

    const searchReports = async () => {
        if (!selectedProject) {
            showMessage("Please select Project");
            return;
        }

        if (!date) {
            showMessage("Please select Date");
            return;
        }

        try {
            setLoading(true);
            setSearched(true);

            const res = await DailyReportService.getApprovedByDate(date);
            console.log(
                "APPROVED REPORTS",
                JSON.stringify(res?.data, null, 2)
            );

            if (res?.status) {
                setReports(res.data || []);
            } else {
                setReports([]);
            }
        } catch (err) {
            console.error("Search reports error:", err);
            showMessage("Failed to load reports");
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const groupReports = () => {

        const grouped = {};

        const filteredReports = reports.filter(r => {
            const projId = r.projectId || r.projectid;
            const locId = r.locationId || r.locationid;

            const matchProject = selectedProject
                ? Number(projId) === Number(selectedProject)
                : true;

            const matchLocation = selectedLocation
                ? Number(locId) === Number(selectedLocation)
                : true;

            return matchProject && matchLocation;
        });

        filteredReports.forEach(r => {

            const category = r.projectCategoryName || "OTHER";
            const projId = r.projectId || r.projectid;

            const key = projId + "_" + category;

            if (!grouped[key]) {

                grouped[key] = {
                    projectId: projId,
                    projectName: r.projectName,
                    category: category,
                    rows: []
                };

            }

            grouped[key].rows.push(r);

        });

        return grouped;

    };


    const exportExcel = () => {

        const emp = StorageService.loginData();
        const grouped = groupReports();

        if (Object.keys(grouped).length === 0) {
            showMessage("No reports found to export");
            return;
        }

        const wb = XLSX.utils.book_new();

        const ws = {};
        ws["!merges"] = [];

        let row = 0;

        const thin = { style: "thin" };
        const thick = { style: "medium" };
        const applyRangeBorder = (sheet, startRow, startCol, endRow, endCol, borderStyle = thin) => {
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    const cellKey = XLSX.utils.encode_cell({ r, c });
                    if (!sheet[cellKey]) sheet[cellKey] = { t: "s", v: "" };
                    sheet[cellKey].s = sheet[cellKey].s || {};
                    const cellBorder = { ...(sheet[cellKey].s.border || {}) };
                    if (r === startRow) cellBorder.top = borderStyle;
                    if (r === endRow) cellBorder.bottom = borderStyle;
                    if (c === startCol) cellBorder.left = borderStyle;
                    if (c === endCol) cellBorder.right = borderStyle;
                    sheet[cellKey].s.border = cellBorder;
                }
            }
        };
        const companyName = StorageService.activeCompany()?.companyName || "Company";

        // Company Name
        XLSX.utils.sheet_add_aoa(ws, [[companyName]], {
            origin: { r: row, c: 1 }
        });

        ws[XLSX.utils.encode_cell({ r: row, c: 1 })].s = {
            font: { bold: true, sz: 12 }
        };

        // Report Date Value
        const exportDateStr = "Date: " + utility.formatExportDateTime(new Date());
        XLSX.utils.sheet_add_aoa(ws, [[exportDateStr]], {
            origin: { r: row, c: 10 }
        });

        ws["!merges"].push({
            s: { r: row, c: 10 },
            e: { r: row, c: 11 }
        });

        ws[XLSX.utils.encode_cell({ r: row, c: 10 })].s = {
            font: { bold: true, sz: 10 },
            alignment: { horizontal: "right" }
        };

        row += 2;

        const center = {
            horizontal: "center",
            vertical: "center",
            wrapText: true
        };

        Object.values(grouped).forEach(group => {
            const isCivil = group.category?.toUpperCase().includes("CIVIL");

            const data = group.rows[0];
            const workStatus =
                (data.workStatus || "")
                    .trim()
                    .toUpperCase();

            const isIdle = workStatus === "IDLE";
            const titleRow = row;

            // =========================
            // TITLE
            // =========================
            XLSX.utils.sheet_add_aoa(ws, [
                ["Daily Progress Report"]
            ], { origin: { r: row, c: 1 } });

            ws["!merges"] = ws["!merges"] || [];

            ws["!merges"].push({
                s: { r: row, c: 1 },
                e: { r: row, c: 11 }
            });

            const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

            ws["!rows"] = ws["!rows"] || [];
            ws["!rows"][row] = { hpt: 36 };

            ws[titleCell].s = {
                font: { bold: true, sz: 14, underline: true },
                alignment: center
            };

            row += 2;

            // =========================
            // PROJECT + DATE
            // =========================
            const projectRow = row;

            ws["!rows"] = ws["!rows"] || [];
            ws["!rows"][projectRow] = { hpt: 26 };
            ws["!rows"][projectRow + 1] = { hpt: 26 };

            const locationNames = [
                ...new Set(
                    group.rows.flatMap(r => {
                        if (r.locationName) {
                            return [r.locationName];
                        }
                        if (r.items?.length > 0) {
                            return r.items.map(i => i.locationName);
                        }
                        return [];
                    })
                )
            ]
                .filter(Boolean)
                .join(", ");

            const reportDateSerial = utility.convertDateToExcelSerial(data.reportDate);

            XLSX.utils.sheet_add_aoa(ws, [
                [
                    "Project Name :",
                    formatProjectDisplay({
                        projectName: data.projectName,
                        projectCategoryName: data.projectCategoryName,
                        projectCode: data.projectCode
                    }),
                    "",
                    "",
                    "Location:",
                    "",
                    locationNames || "-",
                    "",
                    "Report Date :",
                    reportDateSerial || (data.reportDate ? formatDate(data.reportDate) : "-"),
                    ""
                ],
                [
                    "Work Status :",
                    (data.workStatus || "-").toUpperCase(),
                    "",
                    "",
                    "Submitted By:",
                    "",
                    data.createdByName || ((emp?.firstName || "") + " " + (emp?.lastName || "")).trim() || "-",
                    "",
                    "Approved By :",
                    data.approvedByName || "-",
                    ""
                ]
            ], {
                origin: { r: projectRow, c: 1 }
            });

            const dateCell = XLSX.utils.encode_cell({ r: projectRow, c: 10 });
            if (ws[dateCell] && typeof ws[dateCell].v === "number") {
                ws[dateCell].t = "n";
                ws[dateCell].z = "dd-mm-yyyy";
            }

            ws["!merges"].push(
                // Row 1
                { s: { r: projectRow, c: 1 }, e: { r: projectRow, c: 1 } },     // Project Name label
                { s: { r: projectRow, c: 2 }, e: { r: projectRow, c: 4 } },     // Project Name value
                { s: { r: projectRow, c: 5 }, e: { r: projectRow, c: 6 } },     // Location label
                { s: { r: projectRow, c: 7 }, e: { r: projectRow, c: 8 } },     // Location value
                { s: { r: projectRow, c: 9 }, e: { r: projectRow, c: 9 } },     // Report Date label
                { s: { r: projectRow, c: 10 }, e: { r: projectRow, c: 11 } },   // Report Date value

                // Row 2
                { s: { r: projectRow + 1, c: 1 }, e: { r: projectRow + 1, c: 1 } },   // Work Status label
                { s: { r: projectRow + 1, c: 2 }, e: { r: projectRow + 1, c: 4 } },   // Work Status value
                { s: { r: projectRow + 1, c: 5 }, e: { r: projectRow + 1, c: 6 } },   // Submitted By label
                { s: { r: projectRow + 1, c: 7 }, e: { r: projectRow + 1, c: 8 } },   // Submitted By value
                { s: { r: projectRow + 1, c: 9 }, e: { r: projectRow + 1, c: 9 } },   // Approved By label
                { s: { r: projectRow + 1, c: 10 }, e: { r: projectRow + 1, c: 11 } }  // Approved By value
            );

            // Project Details Border & Formatting (applied to merged blocks to avoid inner cut lines)
            const projectBlocks = [
                // Row 1
                { sR: projectRow, sC: 1, eR: projectRow, eC: 1, isLabel: true },
                { sR: projectRow, sC: 2, eR: projectRow, eC: 4, isLabel: false },
                { sR: projectRow, sC: 5, eR: projectRow, eC: 6, isLabel: true },
                { sR: projectRow, sC: 7, eR: projectRow, eC: 8, isLabel: false },
                { sR: projectRow, sC: 9, eR: projectRow, eC: 9, isLabel: true },
                { sR: projectRow, sC: 10, eR: projectRow, eC: 11, isLabel: false },
                // Row 2
                { sR: projectRow + 1, sC: 1, eR: projectRow + 1, eC: 1, isLabel: true },
                { sR: projectRow + 1, sC: 2, eR: projectRow + 1, eC: 4, isLabel: false },
                { sR: projectRow + 1, sC: 5, eR: projectRow + 1, eC: 6, isLabel: true },
                { sR: projectRow + 1, sC: 7, eR: projectRow + 1, eC: 8, isLabel: false },
                { sR: projectRow + 1, sC: 9, eR: projectRow + 1, eC: 9, isLabel: true },
                { sR: projectRow + 1, sC: 10, eR: projectRow + 1, eC: 11, isLabel: false }
            ];

            projectBlocks.forEach(b => {
                applyRangeBorder(ws, b.sR, b.sC, b.eR, b.eC, thin);
                for (let r = b.sR; r <= b.eR; r++) {
                    for (let c = b.sC; c <= b.eC; c++) {
                        const cell = XLSX.utils.encode_cell({ r, c });
                        ws[cell].s = {
                            ...(ws[cell].s || {}),
                            font: { bold: b.isLabel, sz: 10 },
                            alignment: { horizontal: "left", vertical: "center", wrapText: false }
                        };
                    }
                }
            });

            row = projectRow + 3;

            if (isIdle) {
                const idleNoteRow = projectRow + 2;

                XLSX.utils.sheet_add_aoa(ws, [["Idle Note :", data.idleNote || "-"]], {
                    origin: { r: idleNoteRow, c: 1 }
                });

                ws["!merges"].push({
                    s: { r: idleNoteRow, c: 2 },
                    e: { r: idleNoteRow, c: 11 }
                });

                applyRangeBorder(ws, idleNoteRow, 1, idleNoteRow, 1, thin);
                applyRangeBorder(ws, idleNoteRow, 2, idleNoteRow, 11, thin);

                const idleLabelCell = XLSX.utils.encode_cell({ r: idleNoteRow, c: 1 });
                const idleValueCell = XLSX.utils.encode_cell({ r: idleNoteRow, c: 2 });

                ws[idleLabelCell].s = {
                    ...(ws[idleLabelCell].s || {}),
                    font: { bold: true, sz: 10 },
                    alignment: { horizontal: "left", vertical: "center" }
                };

                ws[idleValueCell].s = {
                    ...(ws[idleValueCell].s || {}),
                    alignment: { horizontal: "left", vertical: "center" }
                };

                const endRow = idleNoteRow;

                for (let r = titleRow; r <= endRow; r++) {
                    for (let c = 1; c <= 11; c++) {
                        const cell = XLSX.utils.encode_cell({ r, c });
                        if (!ws[cell]) ws[cell] = { t: "s", v: "" };
                    }
                }

                row = idleNoteRow + 2;
                return;
            }

            const workProgressStartRow = row;

            // =========================
            // WORK PROGRESS TITLE
            // =========================
            XLSX.utils.sheet_add_aoa(
                ws,
                [["Work Progress"]],
                { origin: { r: row, c: 1 } }
            );

            ws["!merges"].push({
                s: { r: row, c: 1 },
                e: { r: row, c: 11 }
            });

            const workProgressTitle = XLSX.utils.encode_cell({ r: row, c: 1 });
            ws[workProgressTitle].s = {
                font: { bold: true, sz: 11, underline: true },
                alignment: center
            };

            row++;

            const header = isCivil
                ? [
                    "Location",
                    "Side",
                    "Work Category",
                    "Work Activity",
                    "UOM",
                    "Nos",
                    "Length",
                    "Breadth",
                    "Depth",
                    "Work Done Qty",
                    "Remarks",
                ]
                : [
                    "Location",
                    "From",
                    "To",
                    "Side",
                    "Work Category",
                    "Work Activity",
                    "UOM",
                    "Work Done Qty",
                    "Remarks"
                ];

            if (isCivil) {
                XLSX.utils.sheet_add_aoa(ws, [
                    [
                        "Location",
                        "Side",
                        "Work Category",
                        "Work Activity",
                        "UOM",
                        "Measurement",
                        "",
                        "",
                        "",
                        "Work Done Qty",
                        "Remarks"
                    ],
                    [
                        "",
                        "",
                        "",
                        "",
                        "",
                        "Nos",
                        "Length",
                        "Breadth",
                        "Depth",
                        "",
                        ""
                    ]
                ], { origin: { r: row, c: 1 } });

                ws["!merges"].push(
                    { s: { r: row, c: 1 }, e: { r: row + 1, c: 1 } }, // Location
                    { s: { r: row, c: 2 }, e: { r: row + 1, c: 2 } }, // Side
                    { s: { r: row, c: 3 }, e: { r: row + 1, c: 3 } }, // Work Category
                    { s: { r: row, c: 4 }, e: { r: row + 1, c: 4 } }, // Work Activity
                    { s: { r: row, c: 5 }, e: { r: row + 1, c: 5 } }, // UOM
                    { s: { r: row, c: 6 }, e: { r: row, c: 9 } },     // Measurement
                    { s: { r: row, c: 10 }, e: { r: row + 1, c: 10 } }, // Work Done Qty
                    { s: { r: row, c: 11 }, e: { r: row + 1, c: 11 } }  // Remarks
                );

                const civilHeaderBlocks = [
                    { sR: row, sC: 1, eR: row + 1, eC: 1 }, // Location
                    { sR: row, sC: 2, eR: row + 1, eC: 2 }, // Side
                    { sR: row, sC: 3, eR: row + 1, eC: 3 }, // Work Category
                    { sR: row, sC: 4, eR: row + 1, eC: 4 }, // Work Activity
                    { sR: row, sC: 5, eR: row + 1, eC: 5 }, // UOM
                    { sR: row, sC: 6, eR: row, eC: 9 },     // Measurement
                    { sR: row + 1, sC: 6, eR: row + 1, eC: 6 }, // Nos
                    { sR: row + 1, sC: 7, eR: row + 1, eC: 7 }, // Length
                    { sR: row + 1, sC: 8, eR: row + 1, eC: 8 }, // Breadth
                    { sR: row + 1, sC: 9, eR: row + 1, eC: 9 }, // Depth
                    { sR: row, sC: 10, eR: row + 1, eC: 10 }, // Work Done Qty
                    { sR: row, sC: 11, eR: row + 1, eC: 11 }  // Remarks
                ];

                civilHeaderBlocks.forEach(b => {
                    applyRangeBorder(ws, b.sR, b.sC, b.eR, b.eC, thin);
                    for (let r = b.sR; r <= b.eR; r++) {
                        for (let c = b.sC; c <= b.eC; c++) {
                            const cell = XLSX.utils.encode_cell({ r, c });
                            ws[cell].s = {
                                ...(ws[cell].s || {}),
                                font: { bold: true, sz: 10 },
                                fill: { fgColor: { rgb: "D9EAD3" } },
                                alignment: center
                            };
                        }
                    }
                });

                row += 2;

            } else {
                XLSX.utils.sheet_add_aoa(ws, [header], {
                    origin: { r: row, c: 1 }
                });

                for (let c = 1; c <= header.length; c++) {
                    const cell = XLSX.utils.encode_cell({ r: row, c });
                    if (!ws[cell]) ws[cell] = { t: "s", v: "" };
                    ws[cell].s = {
                        font: { bold: true, sz: 10 },
                        fill: { fgColor: { rgb: "D9EAD3" } },
                        alignment: center,
                        border: { top: thin, bottom: thin, left: thin, right: thin }
                    };
                }

                row++;
            }

            group.rows.forEach(r => {
                const workRows =
                    r.items?.length > 0
                        ? r.items
                        : [r];

                workRows.forEach(w => {
                    const rowData = isCivil
                        ? [
                            w.locationName,
                            w.side || "-",
                            w.workItemCode,
                            w.workActivity || "-",
                            w.UOM,
                            Number(w.nos || 0),
                            Number(w.length || 0),
                            Number(w.breadth || 0),
                            Number(w.depth || 0),
                            Number(w.workQuantity || w.quantity || 0),
                            w.remarks
                        ]
                        : [
                            w.locationName,
                            Number(w.fromChainage || 0),
                            Number(w.toChainage || 0),
                            w.side || "-",
                            w.workItemCode,
                            w.workActivity || "-",
                            w.UOM,
                            Number(w.workQuantity || w.quantity || 0),
                            w.remarks
                        ];

                    XLSX.utils.sheet_add_aoa(
                        ws,
                        [rowData],
                        { origin: { r: row, c: 1 } }
                    );

                    if (isCivil) {
                        [6, 7, 8, 9, 10].forEach(col => {
                            const cKey = XLSX.utils.encode_cell({ r: row, c: col });
                            if (ws[cKey] && typeof ws[cKey].v === 'number') {
                                ws[cKey].t = 'n';
                                ws[cKey].z = col === 6 ? '#,##0' : '0.000';
                            }
                        });
                    } else {
                        [2, 3, 8].forEach(col => {
                            const cKey = XLSX.utils.encode_cell({ r: row, c: col });
                            if (ws[cKey] && typeof ws[cKey].v === 'number') {
                                ws[cKey].t = 'n';
                                ws[cKey].z = col === 8 ? '0.000' : '0.000';
                            }
                        });
                    }

                    ws["!rows"] = ws["!rows"] || [];
                    ws["!rows"][row] = { hpt: 22 };

                    row++;
                });
            });

            const endRow = row - 1;

            // Apply Borders and Alignment to Work Progress data
            const colCount = isCivil ? 11 : header.length;
            const dataStartRow = workProgressStartRow + (isCivil ? 3 : 2);

            for (let r = dataStartRow; r <= endRow; r++) {
                for (let c = 1; c <= colCount; c++) {
                    const cell = XLSX.utils.encode_cell({ r, c });
                    if (!ws[cell]) {
                        ws[cell] = { t: "s", v: "" };
                    }
                    ws[cell].s = ws[cell].s || {};

                    if (isCivil) {
                        if ([6, 7, 8, 9, 10].includes(c)) {
                            ws[cell].s.alignment = { horizontal: "right", vertical: "center" };
                        } else if (c === 5) {
                            ws[cell].s.alignment = { horizontal: "center", vertical: "center" };
                        } else {
                            ws[cell].s.alignment = { horizontal: "left", vertical: "center" };
                        }
                    } else {
                        if ([2, 3, 8].includes(c)) {
                            ws[cell].s.alignment = { horizontal: "right", vertical: "center" };
                        } else if (c === 7) {
                            ws[cell].s.alignment = { horizontal: "center", vertical: "center" };
                        } else {
                            ws[cell].s.alignment = { horizontal: "left", vertical: "center" };
                        }
                    }

                    ws[cell].s.border = {
                        top: thin,
                        bottom: thin,
                        left: thin,
                        right: thin
                    };
                }
            }

            row += 1;

            const allMaterialItems =
                group.rows.flatMap(
                    r => r.materialItems || []
                );

            let materialLastRow = endRow;
            let labourLastRow = endRow;

            if (!isIdle && allMaterialItems.length > 0) {
                XLSX.utils.sheet_add_aoa(
                    ws,
                    [["Material Consumption"]],
                    { origin: { r: row, c: 1 } }
                );

                ws["!merges"].push({
                    s: { r: row, c: 1 },
                    e: { r: row, c: 7 }
                });

                const materialTitleCell = XLSX.utils.encode_cell({ r: row, c: 1 });
                ws[materialTitleCell].s = {
                    font: { bold: true, sz: 11, underline: true },
                    alignment: center
                };

                row++;

                XLSX.utils.sheet_add_aoa(
                    ws,
                    [[
                        "Outward Voucher No",
                        "Item Category",
                        "Item",
                        "Consumption Qty",
                        "Lost Qty",
                        "Damaged Qty",
                        "Returned Qty"
                    ]],
                    { origin: { r: row, c: 1 } }
                );

                for (let c = 1; c <= 7; c++) {
                    const cell = XLSX.utils.encode_cell({ r: row, c });
                    ws[cell] = ws[cell] || { t: "s", v: "" };
                    ws[cell].s = {
                        font: { bold: true, sz: 10 },
                        fill: { fgColor: { rgb: "D9EAD3" } },
                        alignment: center,
                        border: { top: thin, bottom: thin, left: thin, right: thin }
                    };
                }

                row++;

                const materialStartRow = row;

                allMaterialItems.forEach(m => {
                    XLSX.utils.sheet_add_aoa(
                        ws,
                        [
                            [
                                m.stockOutVoucherNo || "-",
                                m.itemCategoryName || "-",
                                m.itemCode || m.itemName || "-",
                                Number(m.quantityUsed || 0),
                                Number(m.quantityLost || 0),
                                Number(m.quantityDamaged || 0),
                                Number(m.quantityReturned || 0)
                            ]
                        ],
                        { origin: { r: row, c: 1 } }
                    );

                    [4, 5, 6, 7].forEach(col => {
                        const cell = XLSX.utils.encode_cell({ r: row, c: col });
                        if (ws[cell] && typeof ws[cell].v === "number") {
                            ws[cell].t = "n";
                            ws[cell].z = "0.000";
                        }
                    });

                    row++;
                });

                const materialEndRow = row - 1;
                materialLastRow = materialEndRow;

                for (let r = materialStartRow; r <= materialEndRow; r++) {
                    for (let c = 1; c <= 7; c++) {
                        const cell = XLSX.utils.encode_cell({ r, c });
                        ws[cell] = ws[cell] || { t: "s", v: "" };
                        const isNumericCol = [4, 5, 6, 7].includes(c);
                        ws[cell].s = {
                            alignment: {
                                horizontal: isNumericCol ? "right" : "left",
                                vertical: "center"
                            },
                            border: { top: thin, bottom: thin, left: thin, right: thin }
                        };
                    }
                }

                row += 1;
            }

            const allLabourItems =
                group.rows.flatMap(
                    r => r.labourItems || []
                );

            const labourRowStart = row;

            if (!isIdle && allLabourItems.length > 0) {
                XLSX.utils.sheet_add_aoa(
                    ws,
                    [["Labour Details"]],
                    { origin: { r: labourRowStart, c: 1 } }
                );

                ws["!merges"].push({
                    s: { r: labourRowStart, c: 1 },
                    e: { r: labourRowStart, c: 5 }
                });

                const labourTitleCell = XLSX.utils.encode_cell({ r: labourRowStart, c: 1 });
                ws[labourTitleCell].s = {
                    font: { bold: true, sz: 11, underline: true },
                    alignment: center
                };

                row++;

                XLSX.utils.sheet_add_aoa(
                    ws,
                    [[
                        "Vendor",
                        "Skilled Labour",
                        "Unskilled Labour",
                        "Total Labour",
                        "Notes"
                    ]],
                    { origin: { r: row, c: 1 } }
                );

                for (let c = 1; c <= 5; c++) {
                    const cell = XLSX.utils.encode_cell({ r: row, c });
                    ws[cell] = ws[cell] || { t: "s", v: "" };
                    ws[cell].s = {
                        font: { bold: true, sz: 10 },
                        fill: { fgColor: { rgb: "D9EAD3" } },
                        alignment: center,
                        border: { top: thin, bottom: thin, left: thin, right: thin }
                    };
                }

                row++;

                const labourStartRow = row;
                let totalSkilled = 0;
                let totalUnskilled = 0;

                allLabourItems.forEach(l => {
                    const skilled = Number(l.skilledLabour || 0);
                    const unskilled = Number(l.unSkilledLabour || 0);
                    const rowTotal = skilled + unskilled;
                    totalSkilled += skilled;
                    totalUnskilled += unskilled;

                    XLSX.utils.sheet_add_aoa(
                        ws,
                        [
                            [
                                l.vendorName || "-",
                                skilled,
                                unskilled,
                                rowTotal,
                                l.notes || ""
                            ]
                        ],
                        { origin: { r: row, c: 1 } }
                    );

                    [2, 3, 4].forEach(col => {
                        const cell = XLSX.utils.encode_cell({ r: row, c: col });
                        if (ws[cell] && typeof ws[cell].v === "number") {
                            ws[cell].t = "n";
                            ws[cell].z = "0";
                        }
                    });

                    row++;
                });

                const labourDataEndRow = row - 1;

                for (let r = labourStartRow; r <= labourDataEndRow; r++) {
                    for (let c = 1; c <= 5; c++) {
                        const cell = XLSX.utils.encode_cell({ r: r, c });
                        ws[cell] = ws[cell] || { t: "s", v: "" };
                        const isNumericCol = [2, 3, 4].includes(c);
                        ws[cell].s = {
                            alignment: {
                                horizontal: isNumericCol ? "right" : "left",
                                vertical: "center"
                            },
                            border: { top: thin, bottom: thin, left: thin, right: thin }
                        };
                    }
                }

                // Total Row for Labour
                const grandLabourTotal = totalSkilled + totalUnskilled;
                XLSX.utils.sheet_add_aoa(
                    ws,
                    [[
                        "Total:",
                        totalSkilled,
                        totalUnskilled,
                        grandLabourTotal,
                        ""
                    ]],
                    { origin: { r: row, c: 1 } }
                );

                for (let c = 1; c <= 5; c++) {
                    const cell = XLSX.utils.encode_cell({ r: row, c });
                    ws[cell] = ws[cell] || { t: "s", v: "" };
                    const isNumericCol = [2, 3, 4].includes(c);
                    ws[cell].s = {
                        font: { bold: true },
                        alignment: {
                            horizontal: c === 1 ? "left" : (isNumericCol ? "right" : "left"),
                            vertical: "center"
                        },
                        border: { top: thin, bottom: thin, left: thin, right: thin }
                    };
                    if (isNumericCol && typeof ws[cell].v === "number") {
                        ws[cell].t = "n";
                        ws[cell].z = "0";
                    }
                }

                labourLastRow = row;
                row++;
            }

            const lastRow = Math.max(
                endRow,
                materialLastRow,
                labourLastRow
            );

            // ===== OUTER BORDER AROUND REPORT BLOCK =====
            for (let r = titleRow; r <= lastRow; r++) {
                let leftCell = XLSX.utils.encode_cell({ r, c: 1 });
                if (!ws[leftCell]) ws[leftCell] = { t: "s", v: "" };
                ws[leftCell].s = ws[leftCell].s || {};
                ws[leftCell].s.border = {
                    ...(ws[leftCell].s.border || {}),
                    left: thick
                };

                let rightCell = XLSX.utils.encode_cell({ r, c: 11 });
                if (!ws[rightCell]) ws[rightCell] = { t: "s", v: "" };
                ws[rightCell].s = ws[rightCell].s || {};
                ws[rightCell].s.border = {
                    ...(ws[rightCell].s.border || {}),
                    right: thick
                };
            }

            for (let c = 1; c <= 11; c++) {
                let cell = XLSX.utils.encode_cell({ r: titleRow, c });
                if (!ws[cell]) ws[cell] = { t: "s", v: "" };
                ws[cell].s = ws[cell].s || {};
                ws[cell].s.border = {
                    ...(ws[cell].s.border || {}),
                    top: thick
                };
            }

            for (let c = 1; c <= 11; c++) {
                const cell = XLSX.utils.encode_cell({ r: lastRow, c });
                if (!ws[cell]) ws[cell] = { t: "s", v: "" };
                ws[cell].s = ws[cell].s || {};
                ws[cell].s.border = {
                    ...(ws[cell].s.border || {}),
                    bottom: thick
                };
            }

            row = lastRow + 3;
        });

        // =========================
        // AUTO COLUMN WIDTH
        // =========================
        ws["!cols"] = [
            { wch: 4 },   // A blank spacer
            { wch: 22 },  // B (c:1) Location / Outward Voucher / Vendor / Project Name label
            { wch: 14 },  // C (c:2) Side / Item Category / Skilled Labour
            { wch: 22 },  // D (c:3) Work Category / Item / Unskilled Labour
            { wch: 20 },  // E (c:4) Work Activity / Consumption Qty / Notes
            { wch: 12 },  // F (c:5) UOM / Lost Qty / Location & Submitted By label
            { wch: 12 },  // G (c:6) Nos / Damaged Qty
            { wch: 14 },  // H (c:7) Length / Returned Qty
            { wch: 14 },  // I (c:8) Breadth
            { wch: 14 },  // J (c:9) Depth / Report Date & Approved By label
            { wch: 16 },  // K (c:10) Work Done Qty
            { wch: 30 },  // L (c:11) Remarks
        ];

        XLSX.utils.book_append_sheet(wb, ws, "DailyProgressReport");

        const today = new Date();
        const formattedDate =
            String(today.getDate()).padStart(2, "0") + "-" +
            String(today.getMonth() + 1).padStart(2, "0") + "-" +
            today.getFullYear();

        const fileName = `DPR_${formattedDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const exportPdf = async () => {
        const emp = StorageService.loginData();
        const grouped = groupReports();

        if (Object.keys(grouped).length === 0) {
            showMessage("No reports found to export");
            return;
        }

        const doc = new jsPDF("l", "mm", "a4");

        const companyName =
            StorageService.activeCompany()?.companyName || "Company";

        let y = 15;

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const logoWidth = 10;
        const logoHeight = 14;
        const companyX = 14;
        const companyY = 26;

        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        const textWidth = doc.getTextWidth(companyName);
        const logoX = companyX + (textWidth / 2) - (logoWidth / 2);
        const logoY = 8;

        y = 35;

        const groups = Object.values(grouped);

        for (let index = 0; index < groups.length; index++) {

            const group = groups[index];

            if (index !== 0) {
                y += 10;
                if (y > 160) {
                    doc.addPage();
                    y = 35;
                }
            }

            const data = group.rows[0];

            const isCivil =
                group.category?.toUpperCase().includes("CIVIL");

            const isIdle =
                (data.workStatus || "")
                    .toUpperCase() === "IDLE";

            //----------------------------------
            // Title
            //----------------------------------

            doc.setFontSize(18);
            doc.setFont(undefined, "bold");

            doc.text(
                `Daily Progress Report - ${group.category}`,
                pageWidth / 2,
                y + 8,
                { align: "center" }
            );

            const titleWidth = doc.getTextWidth(
                `Daily Progress Report - ${group.category}`
            );

            doc.setLineWidth(0.3);
            doc.line(
                pageWidth / 2 - titleWidth / 2,
                y + 10,
                pageWidth / 2 + titleWidth / 2,
                y + 10
            );

            y += 18;
            //----------------------------------
            // Project Details
            //----------------------------------

            const locationNames = [
                ...new Set(
                    group.rows.flatMap(r => {

                        if (r.locationName)
                            return [r.locationName];

                        if (r.items?.length > 0)
                            return r.items.map(i => i.locationName);

                        return [];

                    })
                )
            ].join(", ");

            autoTable(doc, {

                startY: y,
                margin: { top: 35 },

                theme: "grid",

                styles: {
                    fontSize: 11,
                    halign: "left",
                    valign: "middle",
                    textColor: [60, 60, 60]
                },
                body: [

                    [
                        "Project Name",
                        formatProjectDisplay({
                            projectName: data.projectName,
                            projectCategoryName: data.projectCategoryName,
                            projectCode: data.projectCode
                        }),
                        "Location",
                        locationNames,
                        "Report Date",
                        formatDate(data.reportDate)
                    ],

                    [
                        "Work Status",
                        data.workStatus,
                        "Submitted By",
                        data.createdByName || ((emp?.firstName || "") + " " + (emp?.lastName || "")).trim() || "-",
                        "Approved By",
                        data.approvedByName || "-"
                    ]

                ],

                columnStyles: {
                    0: {
                        fontStyle: "bold",

                        textColor: [30, 30, 30]
                    },
                    2: {
                        fontStyle: "bold",

                        textColor: [30, 30, 30]
                    },
                    4: {
                        fontStyle: "bold",

                        textColor: [30, 30, 30]
                    }
                }
            });

            y = doc.lastAutoTable.finalY + 8;

            //----------------------------------
            // Idle Note
            //----------------------------------

            if (isIdle) {

                autoTable(doc, {

                    startY: y,
                    margin: { top: 35 },

                    head: [["Idle Note"]],

                    body: [[data.idleNote || "-"]],

                    theme: "grid",

                    headStyles: {
                        fillColor: [217, 234, 211]
                    }

                });

                y = doc.lastAutoTable.finalY + 8;
                continue;  // move to next record, no forced page break
            }

            //----------------------------------
            // Work Progress
            //----------------------------------

            doc.setFontSize(12);

            doc.text("Work Progress", 14, y);

            y += 3;

            let head = [];

            if (isCivil) {

                head = [
                    [
                        { content: "Location", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "Side", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "Work Category", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "Work Activity", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "UOM", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "Measurement", colSpan: 4, styles: { halign: "center" } },
                        { content: "Work Done Qty", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
                        { content: "Remarks", rowSpan: 2, styles: { valign: "middle", halign: "center" } }
                    ],
                    [
                        { content: "Nos", styles: { halign: "center" } },
                        { content: "Length", styles: { halign: "center" } },
                        { content: "Breadth", styles: { halign: "center" } },
                        { content: "Depth", styles: { halign: "center" } }
                    ]
                ];

            } else {

                head = [[
                    "Location",
                    "From",
                    "To",
                    "Side",
                    "Work Category",
                    "Work Activity",
                    "UOM",
                    "Work Done Qty",
                    "Remarks"
                ]];

            }

            const body = [];

            group.rows.forEach(r => {

                const rows =
                    r.items?.length > 0
                        ? r.items
                        : [r];

                rows.forEach(w => {

                    if (isCivil) {

                        body.push([

                            w.locationName,

                            w.side || "-",

                            w.workItemCode,

                            w.workActivity || "-",

                            w.UOM,

                            w.nos,

                            w.length,

                            w.breadth,

                            w.depth,

                            w.workQuantity || w.quantity,

                            w.remarks

                        ]);

                    } else {

                        body.push([

                            w.locationName,

                            w.fromChainage,

                            w.toChainage,

                            w.side || "-",

                            w.workItemCode,

                            w.workActivity || "-",

                            w.UOM,

                            w.workQuantity || w.quantity,

                            w.remarks

                        ]);

                    }

                });

            });

            const columnStyles = isCivil
                ? {
                    0: { halign: "left" },
                    1: { halign: "left" },
                    2: { halign: "left" },
                    3: { halign: "left" },
                    4: { halign: "center" },
                    5: { halign: "right" },
                    6: { halign: "right" },
                    7: { halign: "right" },
                    8: { halign: "right" },
                    9: { halign: "right" },
                    10: { halign: "left" }
                }
                : {
                    0: { halign: "left" },
                    1: { halign: "right" },
                    2: { halign: "right" },
                    3: { halign: "left" },
                    4: { halign: "left" },
                    5: { halign: "left" },
                    6: { halign: "center" },
                    7: { halign: "right" },
                    8: { halign: "left" }
                };

            autoTable(doc, {

                startY: y,
                margin: { top: 35 },

                head,

                body,

                theme: "grid",

                styles: {
                    fontSize: 10,
                    halign: "left",
                    textColor: [0, 0, 0],
                    lineColor: [180, 180, 180],
                    lineWidth: 0.3
                },

                columnStyles,

                headStyles: {
                    fillColor: [230, 230, 230],
                    textColor: [0, 0, 0],
                    fontStyle: "bold",
                    halign: "center",
                    lineColor: [180, 180, 180],
                    lineWidth: 0.3
                }

            });

            y = doc.lastAutoTable.finalY + 8;

            //----------------------------------
            // Material Consumption
            //----------------------------------

            const materials =
                group.rows.flatMap(r => r.materialItems || []);

            if (materials.length > 0) {

                doc.text("Material Consumption", 14, y);

                y += 3;

                autoTable(doc, {

                    startY: y,
                    margin: { top: 35 },

                    theme: "grid",

                    head: [[

                        "Outward Voucher No",
                        "Item Category",
                        "Item",
                        "Consumption Qty",
                        "Lost Qty",
                        "Damaged Qty",
                        "Returned Qty"

                    ]],

                    body: materials.map(m => [

                        m.stockOutVoucherNo || "-",

                        m.itemCategoryName || "-",

                        m.itemCode || m.itemName || "-",

                        m.quantityUsed,

                        m.quantityLost,

                        m.quantityDamaged,

                        m.quantityReturned

                    ]),

                    headStyles: {
                        fillColor: [230, 230, 230],
                        textColor: [0, 0, 0],
                        fontStyle: "bold"
                    },
                    styles: {
                        textColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { halign: "left" },
                        1: { halign: "left" },
                        2: { halign: "left" },
                        3: { halign: "right" },
                        4: { halign: "right" },
                        5: { halign: "right" },
                        6: { halign: "right" }
                    }

                });

                y = doc.lastAutoTable.finalY + 8;

            }

            //----------------------------------
            // Labour
            //----------------------------------

            const labour =
                group.rows.flatMap(r => r.labourItems || []);

            if (labour.length > 0) {

                doc.text("Labour Details", 14, y);

                y += 3;

                const totSkilled = labour.reduce((sum, l) => sum + Number(l.skilledLabour || 0), 0);
                const totUnskilled = labour.reduce((sum, l) => sum + Number(l.unSkilledLabour || 0), 0);
                const grandLabourTotal = totSkilled + totUnskilled;

                autoTable(doc, {

                    startY: y,
                    margin: { top: 35 },

                    theme: "grid",

                    head: [[

                        "Vendor",
                        "Skilled",
                        "Unskilled",
                        "Total Labour",
                        "Notes"

                    ]],

                    body: labour.map(l => {
                        const s = Number(l.skilledLabour || 0);
                        const u = Number(l.unSkilledLabour || 0);
                        return [
                            l.vendorName || "-",
                            s,
                            u,
                            s + u,
                            l.notes || ""
                        ];
                    }),

                    foot: [[
                        "Total",
                        totSkilled,
                        totUnskilled,
                        grandLabourTotal,
                        ""
                    ]],

                    headStyles: {
                        fillColor: [230, 230, 230],
                        textColor: [0, 0, 0],
                        fontStyle: "bold"
                    },
                    footStyles: {
                        fillColor: [240, 240, 240],
                        textColor: [0, 0, 0],
                        fontStyle: "bold"
                    },
                    styles: {
                        textColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { halign: "left" },
                        1: { halign: "right" },
                        2: { halign: "right" },
                        3: { halign: "right" },
                        4: { halign: "left" }
                    }

                });

                y = doc.lastAutoTable.finalY + 8;

            }

        }

        // Draw header on every page and footer on last page
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            await addPdfLogo(doc, logo, logoX, logoY, logoWidth, logoHeight);

            doc.setFontSize(11);
            doc.setFont(undefined, "bold");
            doc.text(companyName, companyX, companyY);

            const reportDateY = 24;   // 26, 27 or 28 try cheyyachu

            doc.text("Report Date:", pageWidth - 70, reportDateY);

            doc.setFont(undefined, "normal");
            doc.text(
                utility.formatExportDateTime(new Date()),
                pageWidth - 45,
                reportDateY
            );

            if (i === pageCount) {
                doc.setFont(undefined, "bold");
                doc.setFontSize(8);
                const footerTop = pageHeight - 15;
                doc.text("Prepared By", 25, footerTop);
                doc.text("Checked By", pageWidth / 2, footerTop, { align: "center" });
                doc.text("Approved By", pageWidth - 25, footerTop, { align: "right" });
            }

            doc.setFont(undefined, "normal");
            doc.setFontSize(8);
            const pageText = `Page ${i} of ${pageCount}`;
            doc.text(pageText, pageWidth / 2, pageHeight - 7, { align: "center" });
        }

        // doc.save("DailyProgressReport.pdf");
        // const fileName = `DPR_${utility.formatDate(new Date())}.pdf`;
        const today = new Date();
        const formattedDate =
            String(today.getDate()).padStart(2, "0") + "-" +
            String(today.getMonth() + 1).padStart(2, "0") + "-" +
            today.getFullYear();

        const fileName = `DPR_${formattedDate}.pdf`;

        doc.save(fileName);

    };


    const grouped = groupReports();

    // const uniqueProjects = [
    //     ...new Map(
    //         reports.map(r => [r.projectid, { id: r.projectid, name: r.projectName }])
    //     ).values()
    // ];


    return (
        <>
            <Breadcrumb pageTitle="Daily Progress Report" secondTitle="Reports" />

            <div className="card p-4">

                <div className="row mb-4">

                    <div className="col-md-3">
                        <label className="form-label fw-bold">Project <span className="text-danger">*</span></label>
                        <select
                            className="form-control"
                            value={selectedProject}
                            onChange={handleProjectChange}
                        >
                            <option value="">Select Project</option>
                            {projects.map(p => (
                                <option key={p.projectId || p.id} value={p.projectId || p.id}>
                                    {formatProjectDisplay(p)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label fw-bold">Location</label>
                        <select
                            className="form-control"
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            disabled={!selectedProject}
                        >
                            <option value="">All Locations</option>
                            {locations.map(loc => (
                                <option key={loc.id || loc.locationId} value={loc.id || loc.locationId}>
                                    {loc.locationName || loc.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label fw-bold">Date <span className="text-danger">*</span></label>
                        <input
                            type="date"
                            className="form-control"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </div>

                    <div className="col-md-3 d-flex align-items-end gap-2">

                        <button
                            className="btn btn-primary"
                            onClick={searchReports}
                            disabled={loading}
                        >
                            <i className="ti ti-search me-1"></i>
                            Search
                        </button>

                        <button
                            className="btn btn-secondary"
                            onClick={handleClearFilters}
                            disabled={loading}
                        >
                            <i className="ti ti-refresh me-1"></i>
                            Clear
                        </button>

                    </div>

                    <div className="mt-3 d-flex justify-content-end align-items-center">
                        <div className="d-flex gap-2">
                            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
                                <i className="ti ti-file-type-pdf me-1"></i>
                                Export PDF
                            </button>

                            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
                                <i className="ti ti-file-spreadsheet me-1"></i>
                                Export Excel
                            </button>
                        </div>
                    </div>


                </div>

                {Object.values(grouped).map(group => (

                    // <div key={group.category} className="mb-5">
                    <div key={group.projectId + "_" + group.category} className="mb-5">

                        <h4>Daily Progress Report - {group.category}</h4>

                        {/* <div className="mb-2">
                            <b>Project :</b> {group.rows[0].projectName}
                        </div>

                        <div className="mb-2">
                            <b>Report Date :</b> {formatDate(group.rows[0].reportDate)}
                        </div>

                        <div className="mb-2">
                            <b>Vendor :</b> {group.rows[0].vendorName}
                        </div>

                        <div className="mb-3">
                            <b>No of Labour :</b>
                            Skilled {group.rows[0].skilledLabour}
                            {" | "}
                            Unskilled {group.rows[0].unskilledLabour}
                        </div> */}

                        <div className="row mb-2">
                            <div className="col-md-4">
                                <b>Project Name :</b>{" "}
                                {formatProjectDisplay({
                                    projectName: group.rows[0].projectName,
                                    projectCategoryName: group.rows[0].projectCategoryName,
                                    projectCode: group.rows[0].projectCode
                                })}
                            </div>

                            <div className="col-md-4">
                                <b>Location :</b> {group.rows[0].locationName || "-"}
                            </div>

                            <div className="col-md-4">
                                <b>Report Date :</b> {formatDate(group.rows[0].reportDate)}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-4">
                                <b>Work Status :</b>{" "}
                                {group.rows[0]?.workStatus || "-"}
                            </div>

                            <div className="col-md-4">
                                <b>Submitted By :</b> {group.rows[0].createdByName || "-"}
                            </div>

                            <div className="col-md-4">
                                <b>Approved By :</b> {group.rows[0].approvedByName || "-"}
                            </div>

                            {group.rows[0]?.workStatus === "IDLE" && (
                                <div className="col-md-12 mt-2">
                                    <b>Idle Note :</b>{" "}
                                    {group.rows[0]?.idleNote || "-"}
                                </div>
                            )}
                        </div>

                        {group.rows[0]?.workStatus !== "IDLE" && (

                            <div className="row mb-3">

                                <div className="col-md-3">
                                    <b>Vendor :</b>{" "}
                                    {
                                        group.rows[0]?.labourItems?.length > 0
                                            ? group.rows[0].labourItems
                                                .map(l => l.vendorName)
                                                .join(", ")
                                            : "-"
                                    }
                                </div>

                                <div className="col-md-3">
                                    <b>No of Labour :</b>{" "}

                                    Skilled {
                                        group.rows[0]?.labourItems?.reduce(
                                            (sum, l) =>
                                                sum + Number(l.skilledLabour || 0),
                                            0
                                        )
                                    }

                                    {" | "}

                                    Unskilled {
                                        group.rows[0]?.labourItems?.reduce(
                                            (sum, l) =>
                                                sum + Number(l.unSkilledLabour || 0),
                                            0
                                        )
                                    }

                                </div>

                            </div>

                        )}

                        {group.rows[0]?.workStatus !== "IDLE" && (
                            <>
                                <h6 className="fw-bold">
                                    Work Progress
                                </h6>

                                <table className="table table-bordered">

                                    <thead>
                                        {group.category?.toUpperCase().includes("CIVIL") ? (
                                            <>
                                                <tr>
                                                    <th rowSpan="2" className="text-start">Location</th>
                                                    <th rowSpan="2" className="text-start">Side</th>
                                                    <th rowSpan="2" className="text-start">Work Category</th>
                                                    <th rowSpan="2" className="text-start">Work Activity</th>
                                                    <th rowSpan="2" className="text-center">UOM</th>
                                                    <th colSpan="4" className="text-center">Measurement</th>
                                                    <th rowSpan="2" className="text-end">Work Done Qty</th>
                                                    <th rowSpan="2" className="text-start">Remarks</th>
                                                </tr>

                                                <tr>
                                                    <th className="text-end">Nos</th>
                                                    <th className="text-end">Length</th>
                                                    <th className="text-end">Breadth</th>
                                                    <th className="text-end">Depth</th>
                                                </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <th className="text-start">Location</th>
                                                <th className="text-end">From</th>
                                                <th className="text-end">To</th>
                                                <th className="text-start">Side</th>
                                                <th className="text-start">Work Category</th>
                                                <th className="text-start">Work Activity</th>
                                                <th className="text-center">UOM</th>
                                                <th className="text-end">Work Done Qty</th>
                                                <th className="text-start">Remarks</th>
                                            </tr>
                                        )}
                                    </thead>

                                    <tbody>

                                        {group.rows.map(r => {

                                            const workRows =
                                                r.items?.length > 0
                                                    ? r.items
                                                    : [r];

                                            return workRows.map((w, index) => (

                                                <tr key={`${r.id}_${index}`}>

                                                    <td className="text-start">{w.locationName}</td>

                                                    {group.category?.toUpperCase().includes("CIVIL") ? (

                                                        <>
                                                            <td className="text-start">{w.side || "-"}</td>
                                                            <td className="text-start">{w.workItemCode}</td>
                                                            <td className="text-start">{w.workActivity || "-"}</td>
                                                            <td className="text-center">{w.UOM}</td>
                                                            <td className="text-end">{w.nos ?? ""}</td>
                                                            <td className="text-end">{w.length ?? ""}</td>
                                                            <td className="text-end">{w.breadth ?? ""}</td>
                                                            <td className="text-end">{w.depth ?? ""}</td>
                                                            <td className="text-end">
                                                                {w.workQuantity || w.quantity}
                                                            </td>
                                                            <td className="text-start">{w.remarks}</td>
                                                        </>

                                                    ) : (

                                                        <>
                                                            <td className="text-end">{w.fromChainage}</td>

                                                            <td className="text-end">{w.toChainage}</td>

                                                            <td className="text-start">{w.side || "-"}</td>

                                                            <td className="text-start">{w.workItemCode}</td>

                                                            <td className="text-start">{w.workActivity || "-"}</td>

                                                            <td className="text-center">{w.UOM}</td>

                                                            <td className="text-end">
                                                                {w.workQuantity || w.quantity}
                                                            </td>

                                                            <td className="text-start">{w.remarks}</td>
                                                        </>

                                                    )}

                                                </tr>

                                            ));

                                        })}

                                    </tbody>

                                </table>

                            </>
                        )}

                        {group.rows[0]?.workStatus !== "IDLE" &&
                            group.rows.some(
                                r => r.materialItems?.length > 0
                            ) && (
                                <>
                                    <h5 className="mt-3">Material Consumption</h5>

                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th className="text-start">Outward Voucher No</th>
                                                <th className="text-start">Item Category</th>
                                                <th className="text-start">Item</th>
                                                <th className="text-end">Consumption Qty</th>
                                                <th className="text-end">Lost Qty</th>
                                                <th className="text-end">Damaged Qty</th>
                                                <th className="text-end">Returned Qty</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {group.rows.flatMap(r =>
                                                (r.materialItems || []).map((m, idx) => (
                                                    <tr key={`mat_${r.id}_${idx}`}>
                                                        <td className="text-start">{m.stockOutVoucherNo || "-"}</td>
                                                        <td className="text-start">{m.itemCategoryName || "-"}</td>
                                                        <td className="text-start">{m.itemCode || m.itemName || "-"}</td>
                                                        <td className="text-end">{m.quantityUsed}</td>
                                                        <td className="text-end">{m.quantityLost}</td>
                                                        <td className="text-end">{m.quantityDamaged}</td>
                                                        <td className="text-end">{m.quantityReturned}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </>
                            )}

                        {/* {group.rows.some(r => r.labourItems?.length > 0) && ( */}
                        {group.rows[0]?.workStatus !== "IDLE" &&
                            group.rows.some(
                                r => r.labourItems?.length > 0
                            ) && (
                                <>
                                    <h5 className="mt-3">Labour Details</h5>

                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th className="text-start">Vendor</th>
                                                <th className="text-end">Skilled Labour</th>
                                                <th className="text-end">Unskilled Labour</th>
                                                <th className="text-end">Total Labour</th>
                                                <th className="text-start">Notes</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {group.rows.flatMap(r =>
                                                (r.labourItems || []).map((l, idx) => {
                                                    const skilled = Number(l.skilledLabour || 0);
                                                    const unskilled = Number(l.unSkilledLabour || 0);
                                                    const rowTotal = skilled + unskilled;
                                                    return (
                                                        <tr key={`lab_${r.id}_${idx}`}>
                                                            <td className="text-start">{l.vendorName}</td>
                                                            <td className="text-end">{l.skilledLabour}</td>
                                                            <td className="text-end">{l.unSkilledLabour}</td>
                                                            <td className="text-end fw-bold">{rowTotal}</td>
                                                            <td className="text-start">{l.notes}</td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>

                                        <tfoot>
                                            {(() => {
                                                const allItems = group.rows.flatMap(r => r.labourItems || []);
                                                const totSkilled = allItems.reduce((sum, l) => sum + Number(l.skilledLabour || 0), 0);
                                                const totUnskilled = allItems.reduce((sum, l) => sum + Number(l.unSkilledLabour || 0), 0);
                                                const grandLabourTotal = totSkilled + totUnskilled;
                                                return (
                                                    <tr className="table-light fw-bold">
                                                        <td className="text-start">Total</td>
                                                        <td className="text-end">{totSkilled}</td>
                                                        <td className="text-end">{totUnskilled}</td>
                                                        <td className="text-end">{grandLabourTotal}</td>
                                                        <td></td>
                                                    </tr>
                                                );
                                            })()}
                                        </tfoot>
                                    </table>
                                </>
                            )}

                    </div>

                ))}

                {searched && Object.keys(grouped).length === 0 && (
                    <div className="text-center mt-4">
                        <h5>No Report for Selected Filters</h5>
                    </div>
                )}

            </div>

            <MessageDialog
                message={message}
                type={msgType}
                setMessage={setMessage}
                setMsgType={setMsgType}
            />
        </>
    );

}